import * as React from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    DialogTitle,
    DialogContent,
    Button,
    DialogActions,
    FormHelperText,
} from '@mui/material';
import pxToRem from 'assets/theme/function/px-to-rem';
import { memo, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { CreateProductPriceRequest } from 'types/product/product-price';
import { useProductPricePaymentModeQuery } from 'store/slices/product/product-enum';
import { useRuleProductListQuery } from 'store/slices/rule/rule-entities-enum';
import rgba from 'assets/theme/function/rgba';
import colors from 'assets/theme/base/colors';
import DialogIconClose from 'component/dialog-icon-close';
import { useAddProductPriceMutation } from 'store/slices/product/product-price';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch } from 'hooks/redux';
import SuccessSnackbarEffect from 'component/success-snackbar';

interface Props {
    currencyList?: string[];
    productId?: string;
    dataProductId?: string;
    setOpenDialogAddProductPrice: React.Dispatch<React.SetStateAction<boolean>>;
    getPriceList: () => void;
}

const CreateProductPrice: React.FC<Props> = memo((props: Props) => {
    const {
        currencyList,
        productId,
        dataProductId,
        getPriceList,
        setOpenDialogAddProductPrice,
    } = props;
    const {
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm<CreateProductPriceRequest>();
    const { data: dataPaymentMod } = useProductPricePaymentModeQuery();
    const { data: ruleProductList } = useRuleProductListQuery({
        entity: 'product-price',
    });

    const [creatProductPriceApi, { isSuccess, error, isError }] =
        useAddProductPriceMutation();

    const formRef = useRef<HTMLFormElement>(null);

    const dispatch = useAppDispatch();

    const onSubmit = (values: CreateProductPriceRequest) => {
        creatProductPriceApi({
            productId: productId ?? dataProductId,
            price: Number(values.price),
            discountPercent: Number(values.discountPercent),
            paymentMode: values.paymentMode,
            currency: values.currency,
            productPriceId: values.productPriceId,
            ruleId: values.ruleId,
        });
    };

    const closeDialog = () => {
        setOpenDialogAddProductPrice(false);
        reset();
    };

    useEffect(() => {
        if (isSuccess) {
            closeDialog();
            getPriceList();
        }
    }, [isSuccess]);

    useEffect(() => {
        if (error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error?.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
        if (isSuccess) {
            dispatch(
                openSnackbar({
                    message: productId
                        ? 'Product Price Update'
                        : 'Product Price Created',
                    severity: 'info',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
            closeDialog();
        }
    }, [isError, isSuccess]);

    return (
        <>
            <Box
                display={'flex'}
                justifyContent={'center'}
                bgcolor={rgba(colors.primary.main, 0.5)}
            >
                <DialogTitle textTransform={'uppercase'}>
                    ADD NEW PRICE
                </DialogTitle>
                <DialogIconClose handleClose={closeDialog} />
            </Box>

            <DialogContent>
                <Box
                    component="form"
                    ref={formRef}
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Box display={'flex'} pt={pxToRem(15)}>
                        <FormControl
                            error={Boolean(errors?.currency)}
                            sx={{ ml: pxToRem(10) }}
                            fullWidth
                        >
                            <InputLabel>Currency</InputLabel>
                            <Controller
                                name="currency"
                                control={control}
                                rules={{
                                    required: 'Currency is required',
                                }}
                                render={({
                                    field: { name, value, onChange },
                                }) => (
                                    <Select
                                        label="Currency"
                                        name={name}
                                        value={value || ''}
                                        onChange={onChange}
                                    >
                                        {currencyList?.map((category) => (
                                            <MenuItem
                                                key={category.toString()}
                                                value={category.toString()}
                                            >
                                                {category}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            {errors?.currency && (
                                <FormHelperText>
                                    {errors?.currency?.message?.toString()}
                                </FormHelperText>
                            )}
                        </FormControl>

                        <FormControl
                            error={Boolean(errors?.paymentMode)}
                            sx={{ ml: pxToRem(10) }}
                            fullWidth
                        >
                            <InputLabel>Payment Mode</InputLabel>
                            <Controller
                                name="paymentMode"
                                control={control}
                                rules={{
                                    required: 'Payment Mode is required',
                                }}
                                render={({
                                    field: { name, value, onChange },
                                }) => (
                                    <Select
                                        label="Payment Mode"
                                        name={name}
                                        value={value || ''}
                                        onChange={onChange}
                                    >
                                        {dataPaymentMod?.map((category) => (
                                            <MenuItem
                                                key={category.toString()}
                                                value={category.toString()}
                                            >
                                                {category}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            {errors?.paymentMode && (
                                <FormHelperText>
                                    {errors?.paymentMode?.message?.toString()}
                                </FormHelperText>
                            )}
                        </FormControl>
                    </Box>

                    <Box display={'flex'} pt={pxToRem(15)}>
                        <Controller
                            name="price"
                            control={control}
                            rules={{
                                required: 'Price Title is required',
                                pattern: {
                                    value: /^[0-9]*[.,]?[0-9]*$/,
                                    message: 'Only numbers are allowed',
                                },
                            }}
                            render={({ field: { name, value, onChange } }) => (
                                <TextField
                                    sx={{ ml: pxToRem(10) }}
                                    fullWidth
                                    name={name}
                                    value={value ?? ''}
                                    onChange={(e) => {
                                        let val = e.target.value;
                                        val = val.replace(',', '.');
                                        if (/^[0-9]*\.?[0-9]*$/.test(val)) {
                                            onChange(val);
                                        }
                                    }}
                                    label="Price"
                                    error={Boolean(errors[name])}
                                    helperText={
                                        errors[name]?.message?.toString() || ''
                                    }
                                    slotProps={{
                                        input: {
                                            inputMode: 'decimal',
                                        },
                                    }}
                                />
                            )}
                        />

                        <Controller
                            name="discountPercent"
                            control={control}
                            rules={{
                                required: 'Discount Percent Title is required',
                                pattern: {
                                    value: /^[0-9]*[.,]?[0-9]*$/,
                                    message: 'Only numbers are allowed',
                                },
                            }}
                            render={({ field: { name, value, onChange } }) => (
                                <TextField
                                    sx={{ ml: pxToRem(10) }}
                                    fullWidth
                                    name={name}
                                    value={value || ''}
                                    onChange={(e) => {
                                        let val = e.target.value;
                                        val = val.replace(',', '.');
                                        if (/^[0-9]*\.?[0-9]*$/.test(val)) {
                                            onChange(val);
                                        }
                                    }}
                                    label="Discount Percent"
                                    error={Boolean(errors[name])}
                                    helperText={
                                        errors[name]?.message?.toString() || ''
                                    }
                                    slotProps={{
                                        input: {
                                            inputMode: 'decimal',
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>

                    <Box display={'flex'} pt={pxToRem(15)}>
                        <FormControl sx={{ ml: pxToRem(10) }} fullWidth>
                            <InputLabel>Rule Id</InputLabel>
                            <Controller
                                name="ruleId"
                                control={control}
                                render={({
                                    field: { name, value, onChange },
                                }) => (
                                    <Select
                                        label="Rule Id"
                                        name={name}
                                        value={value || ''}
                                        onChange={onChange}
                                    >
                                        {ruleProductList?.map((category) => (
                                            <MenuItem
                                                key={category.id.toString()}
                                                value={category.id.toString()}
                                            >
                                                {category.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                        </FormControl>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button
                    sx={{ width: '50%' }}
                    onClick={() => formRef.current?.requestSubmit()}
                    variant={'contained'}
                >
                    Create Product Price
                </Button>
                <Button
                    sx={{ width: '50%', bgcolor: colors.action.menuSelect }}
                    onClick={() => closeDialog()}
                    variant={'contained'}
                >
                    Cancel
                </Button>
            </DialogActions>

            <SuccessSnackbarEffect
                isSuccess={isSuccess}
                message={'Successfully Created an Product Price!'}
            />
        </>
    );
});

export default CreateProductPrice;
